import { useState, useRef, useCallback, useEffect } from "react";
import { authedFetch } from "@/lib/auth";
import { handleAuthError } from "@/lib/trpc-error";
import { readSSEStream } from "@/lib/sse-reader";
import type { WalkthroughStreamEvent } from "@iamu/shared";

import type { TargetBox } from "@/components/walkthrough/screenshot-types";

const MAX_RECONNECT_ATTEMPTS = 10;
const INITIAL_RECONNECT_DELAY_MS = 1_000;
const MAX_RECONNECT_DELAY_MS = 30_000;
const CONNECTION_TIMEOUT_MS = 30_000;

export type WalkthroughStreamStatus =
  | "idle"
  | "connecting"
  | "streaming"
  | "reconnecting"
  | "done"
  | "error";

interface StepData {
  stepNumber: number;
  title: string;
  personaReasoning: string;
  grounding: string | null;
  screenshotPath: string | null;
  action: string | null;
  findings: Array<{
    type: string;
    severity: string;
    description: string;
    recommendation?: string | null;
    targetBox?: TargetBox | null;
  }>;
}

export interface ActionIndicator {
  stepNumber: number;
  actionType: string;
  selector: string | null;
  startedAt?: number;
}

interface LensProgress {
  lensId: string;
  lensLabel: string;
  status: "analyzing" | "complete" | "failed";
  findingCount?: number;
  error?: string;
}

interface CompletionInfo {
  terminationReason: string;
  verdict: { question: string; answer: string } | null;
  whatWorked: string[];
  openQuestions: string[];
}

/** A preliminary finding streamed from a local run followed live. */
export type LiveFinding = Extract<
  WalkthroughStreamEvent,
  { type: "finding_recorded" }
>["finding"];

export type LivePhase = Extract<
  WalkthroughStreamEvent,
  { type: "phase_changed" }
>["phase"];

export interface RunnerInfo {
  id: string;
  name: string;
}

export interface UseWalkthroughStreamReturn {
  steps: StepData[];
  status: WalkthroughStreamStatus;
  activeAction: ActionIndicator | null;
  stepStartedAt: number;
  lastEventAt: number | null;
  isStalled: boolean;
  completion: CompletionInfo | null;
  lensProgress: LensProgress[];
  runner: RunnerInfo | null;
  /** Local runs only: preliminary findings and the post-walk phase. */
  liveFindings: LiveFinding[];
  phase: LivePhase | null;
  error: string | null;
  connect: (walkthroughId: string) => void;
  disconnect: () => void;
}

export function useWalkthroughStream(): UseWalkthroughStreamReturn {
  const [steps, setSteps] = useState<StepData[]>([]);
  const [status, setStatus] = useState<WalkthroughStreamStatus>("idle");
  const [activeAction, setActiveAction] = useState<ActionIndicator | null>(
    null,
  );
  const [stepStartedAt, setStepStartedAt] = useState<number>(Date.now());
  const [lastEventAt, setLastEventAt] = useState<number | null>(null);
  const [isStalled, setIsStalled] = useState(false);
  const [completion, setCompletion] = useState<CompletionInfo | null>(null);
  const [lensProgress, setLensProgress] = useState<LensProgress[]>([]);
  const [runner, setRunner] = useState<RunnerInfo | null>(null);
  const [liveFindings, setLiveFindings] = useState<LiveFinding[]>([]);
  const [phase, setPhase] = useState<LivePhase | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const reconnectRef = useRef<{
    walkthroughId: string;
    attempt: number;
    timer: ReturnType<typeof setTimeout> | null;
  } | null>(null);
  // Track whether a terminal event was received (done/error from server)
  const terminalRef = useRef(false);

  useEffect(() => {
    if (status !== "streaming") {
      setIsStalled(false);
      return;
    }
    const interval = setInterval(() => {
      if (lastEventAt && Date.now() - lastEventAt > 35_000) {
        setIsStalled(true);
      } else {
        setIsStalled(false);
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [status, lastEventAt]);

  const clearReconnect = useCallback(() => {
    if (reconnectRef.current?.timer) {
      clearTimeout(reconnectRef.current.timer);
    }
    reconnectRef.current = null;
  }, []);

  const disconnect = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    clearReconnect();
  }, [clearReconnect]);

  const connectOnce = useCallback(
    (walkthroughId: string, isReconnect: boolean) => {
      // Abort any previous stream
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      // Abort if initial connection takes too long
      const connectionTimer = setTimeout(() => {
        controller.abort();
      }, CONNECTION_TIMEOUT_MS);

      if (!isReconnect) {
        setSteps([]);
        setActiveAction(null);
        setCompletion(null);
        setLensProgress([]);
        setRunner(null);
        setLiveFindings([]);
        setPhase(null);
        setError(null);
        terminalRef.current = false;
      }

      setStatus(isReconnect ? "reconnecting" : "connecting");

      const apiBase = import.meta.env.VITE_API_URL || "";
      const url = `${apiBase}/api/walkthroughs/live/${walkthroughId}/progress`;

      (async () => {
        try {
          const response = await authedFetch(url, {
            method: "GET",
            headers: {
              Accept: "text/event-stream",
            },
            signal: controller.signal,
          });

          if (!response.ok) {
            if (response.status === 401) {
              handleAuthError(new Error("Authentication required"));
              return;
            }
            const err = await response
              .json()
              .catch(() => ({ error: "Request failed" }));
            setError(err.error || `Request failed (${response.status})`);
            setStatus("error");
            return;
          }

          const reader = response.body?.getReader();
          if (!reader) {
            setError("No response stream");
            setStatus("error");
            return;
          }

          // Connected successfully — clear connection timeout and reconnect counter
          clearTimeout(connectionTimer);
          clearReconnect();
          setStatus("streaming");
          setError(null);

          const recordActivity = () => {
            setLastEventAt(Date.now());
            setIsStalled(false);
          };

          await readSSEStream<WalkthroughStreamEvent>(
            reader,
            (event) => {
              recordActivity();
              handleEvent(event);
            },
            () => {
              recordActivity();
            },
          );

          // Stream ended cleanly — if we got a terminal event, stay in that state
          if (terminalRef.current) return;

          // Stream ended without terminal event — walkthrough may still be running, try reconnect
          scheduleReconnect(walkthroughId);
        } catch (err) {
          clearTimeout(connectionTimer);
          // disconnect() sets abortRef to null — only skip reconnect for manual disconnect
          if ((err as Error).name === "AbortError" && !abortRef.current) return;

          // Connection lost or timed out — attempt reconnect
          scheduleReconnect(walkthroughId);
        }
      })();

      function scheduleReconnect(wId: string) {
        const attempt = (reconnectRef.current?.attempt ?? 0) + 1;
        if (attempt > MAX_RECONNECT_ATTEMPTS) {
          setError("Connection lost — too many reconnection attempts");
          setStatus("error");
          return;
        }

        const delay = Math.min(
          INITIAL_RECONNECT_DELAY_MS * 2 ** (attempt - 1),
          MAX_RECONNECT_DELAY_MS,
        );
        setStatus("reconnecting");
        setError(null);

        reconnectRef.current = {
          walkthroughId: wId,
          attempt,
          timer: setTimeout(() => {
            connectOnce(wId, true);
          }, delay),
        };
      }

      function handleEvent(event: WalkthroughStreamEvent) {
        switch (event.type) {
          case "walkthrough_started":
            setStatus("streaming");
            setStepStartedAt(Date.now());
            if (event.runner) {
              setRunner(event.runner);
            }
            break;

          case "step_complete":
            setActiveAction(null);
            setStepStartedAt(Date.now());
            setSteps((prev) => {
              // Deduplicate by stepNumber (catch-up burst may overlap with live events)
              const exists = prev.some(
                (s) => s.stepNumber === event.stepNumber,
              );
              if (exists) return prev;
              return [
                ...prev,
                {
                  stepNumber: event.stepNumber,
                  title: event.title,
                  personaReasoning: event.personaReasoning,
                  grounding: event.grounding,
                  screenshotPath: event.screenshotPath,
                  action: event.action,
                  findings: event.findings.map((f) => ({
                    ...f,
                    targetBox: f.targetBox ?? null,
                  })),
                },
              ];
            });
            break;

          case "action_executing":
            setActiveAction({
              stepNumber: event.stepNumber,
              actionType: event.actionType,
              selector: event.selector,
              startedAt: Date.now(),
            });
            break;

          case "walkthrough_complete":
            terminalRef.current = true;
            setActiveAction(null);
            setCompletion({
              terminationReason: event.terminationReason,
              verdict: event.verdict,
              whatWorked: event.whatWorked,
              openQuestions: event.openQuestions,
            });
            setStatus("done");
            clearReconnect();
            break;

          case "lens_analysis_started":
            setLensProgress((prev) => [
              ...prev.filter((l) => l.lensId !== event.lensId),
              {
                lensId: event.lensId,
                lensLabel: event.lensLabel,
                status: "analyzing",
              },
            ]);
            break;

          case "lens_analysis_complete":
            setLensProgress((prev) =>
              prev.map((l) =>
                l.lensId === event.lensId
                  ? {
                      ...l,
                      status: "complete" as const,
                      findingCount: event.findingCount,
                    }
                  : l,
              ),
            );
            break;

          case "lens_analysis_failed":
            setLensProgress((prev) =>
              prev.map((l) =>
                l.lensId === event.lensId
                  ? { ...l, status: "failed" as const, error: event.error }
                  : l,
              ),
            );
            break;

          case "finding_recorded":
            setLiveFindings((prev) =>
              prev.some((f) => f.id === event.finding.id)
                ? prev.map((f) =>
                    f.id === event.finding.id ? event.finding : f,
                  )
                : [...prev, event.finding],
            );
            break;

          case "phase_changed":
            setPhase(event.phase);
            break;

          case "walkthrough_error":
            terminalRef.current = true;
            setActiveAction(null);
            setError(event.message);
            setStatus("error");
            clearReconnect();
            break;
        }
      }
    },
    [clearReconnect],
  );

  const connect = useCallback(
    (walkthroughId: string) => {
      clearReconnect();
      connectOnce(walkthroughId, false);
    },
    [connectOnce, clearReconnect],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      clearReconnect();
    };
  }, [clearReconnect]);

  return {
    steps,
    status,
    activeAction,
    stepStartedAt,
    lastEventAt,
    isStalled,
    completion,
    lensProgress,
    runner,
    liveFindings,
    phase,
    error,
    connect,
    disconnect,
  };
}
